import React, { useRef, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { getLabels, formatTimestampFromDB } from './DashboardComponent';

const color = '#3498db';
const submenuOptions = ['1s', '5s', '1m', '5m'];

function AccelerationChart({ data, last, interval: propInterval, onIntervalChange }) {
  const [interval, setIntervalValue] = useState(propInterval || '1s');
  const chartRef = useRef();
  const sortedData = [...data].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  
  const chartData = React.useMemo(() => ({
    labels: getLabels(sortedData, interval),
    datasets: [
      // Accelerometru - X, Y, Z
      {
        label: 'Accel X',
        data: sortedData.map(d => {
          const motion = typeof d.motion_sensors === 'string' ? JSON.parse(d.motion_sensors) : d.motion_sensors;
          return (motion?.acceleration_x || 0) * 9.81;
        }),
        borderColor: '#e74c3c',
        backgroundColor: 'rgba(231, 76, 60, 0.1)',
        pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? '#e74c3c' : 'rgba(231,76,60,0.5)'),
        pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 8 : 3),
        pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 3 : 1),
        borderWidth: 2,
        borderDash: [],
      },
      {
        label: 'Accel Y',
        data: sortedData.map(d => {
          const motion = typeof d.motion_sensors === 'string' ? JSON.parse(d.motion_sensors) : d.motion_sensors;
          return (motion?.acceleration_y || 0) * 9.81;
        }),
        borderColor: '#27ae60',
        backgroundColor: 'rgba(39, 174, 96, 0.1)',
        pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? '#27ae60' : 'rgba(39,174,96,0.5)'),
        pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 8 : 3),
        pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 3 : 1),
        borderWidth: 2,
        borderDash: [],
      },
      {
        label: 'Accel Z',
        data: sortedData.map(d => {
          const motion = typeof d.motion_sensors === 'string' ? JSON.parse(d.motion_sensors) : d.motion_sensors;
          return (motion?.acceleration_z || 0) * 9.81;
        }),
        borderColor: '#f39c12',
        backgroundColor: 'rgba(243, 156, 18, 0.1)',
        pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? '#f39c12' : 'rgba(243,156,18,0.5)'),
        pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 8 : 3),
        pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 3 : 1),
        borderWidth: 2,
        borderDash: [],
      },
      // Giroscop - Xg, Yg, Zg (linii întrerupte)
      {
        label: 'Gyro X',
        data: sortedData.map(d => {
          const gyroscope = typeof d.gyroscope_sensors === 'string' ? JSON.parse(d.gyroscope_sensors) : d.gyroscope_sensors;
          return gyroscope?.xg || 0;
        }),
        borderColor: '#e74c3c',
        backgroundColor: 'rgba(231, 76, 60, 0.05)',
        pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? '#e74c3c' : 'rgba(231,76,60,0.3)'),
        pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 6 : 2),
        pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 2 : 1),
        borderWidth: 1.5,
        borderDash: [5, 5],
      },
      {
        label: 'Gyro Y',
        data: sortedData.map(d => {
          const gyroscope = typeof d.gyroscope_sensors === 'string' ? JSON.parse(d.gyroscope_sensors) : d.gyroscope_sensors;
          return gyroscope?.yg || 0;
        }),
        borderColor: '#3498db',
        backgroundColor: 'rgba(52, 152, 219, 0.05)',
        pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? '#3498db' : 'rgba(52,152,219,0.3)'),
        pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 6 : 2),
        pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 2 : 1),
        borderWidth: 1.5,
        borderDash: [5, 5],
      },
      {
        label: 'Gyro Z',
        data: sortedData.map(d => {
          const gyroscope = typeof d.gyroscope_sensors === 'string' ? JSON.parse(d.gyroscope_sensors) : d.gyroscope_sensors;
          return gyroscope?.zg || 0;
        }),
        borderColor: '#f39c12',
        backgroundColor: 'rgba(243, 156, 18, 0.05)',
        pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? '#f39c12' : 'rgba(243,156,18,0.3)'),
        pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 6 : 2),
        pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 2 : 1),
        borderWidth: 1.5,
        borderDash: [5, 5],
      }

    ],
  }), [sortedData, interval]);

  useEffect(() => {
    if (chartRef.current && chartRef.current.chartInstance) {
      const chart = chartRef.current.chartInstance;
      chart.data.labels = chartData.labels;
      chart.data.datasets = chartData.datasets;
      chart.update('none');
    }
  }, [chartData]);

  // Sincronizează intervalul cu prop-ul
  useEffect(() => {
    if (propInterval && propInterval !== interval) {
      setIntervalValue(propInterval);
    }
  }, [propInterval, interval]);

  // LIVE dacă ultima valoare e în ultimele 10 minute
  const isLive = last && (Date.now() - new Date(last.created_at).getTime() < 10 * 60 * 1000);
  
  // Calculează valorile curente pentru accelerometru și giroscop
  const currentValues = last ? (() => {
    const motion = typeof last.motion_sensors === 'string' ? JSON.parse(last.motion_sensors) : last.motion_sensors;
    const gyroscope = typeof last.gyroscope_sensors === 'string' ? JSON.parse(last.gyroscope_sensors) : last.gyroscope_sensors;
    return {
      accelX: (motion?.acceleration_x || 0) * 9.81,
      accelY: (motion?.acceleration_y || 0) * 9.81,
      accelZ: (motion?.acceleration_z || 0) * 9.81,
      gyroX: gyroscope?.xg || 0,
      gyroY: gyroscope?.yg || 0,
      gyroZ: gyroscope?.zg || 0
    };
  })() : { accelX: 0, accelY: 0, accelZ: 0, gyroX: 0, gyroY: 0, gyroZ: 0 };

  const lastTime = last ? formatTimestampFromDB(last.created_at) : '-';

  // Calculează axa Y dinamică pentru accelerometru și giroscop
  const allValues = sortedData.flatMap(d => {
    const motion = typeof d.motion_sensors === 'string' ? JSON.parse(d.motion_sensors) : d.motion_sensors;
    const gyroscope = typeof d.gyroscope_sensors === 'string' ? JSON.parse(d.gyroscope_sensors) : d.gyroscope_sensors;
    return [
      (motion?.acceleration_x || 0) * 9.81,
      (motion?.acceleration_y || 0) * 9.81,
      (motion?.acceleration_z || 0) * 9.81,
      gyroscope?.xg || 0,
      gyroscope?.yg || 0,
      gyroscope?.zg || 0
    ];
  }).filter(v => typeof v === 'number');

  let minY = 0, maxY = 100;
  if (allValues.length) {
    minY = Math.floor(Math.min(...allValues) - 0.1 * Math.abs(Math.min(...allValues)));
    maxY = Math.ceil(Math.max(...allValues) + 0.1 * Math.abs(Math.max(...allValues)));
    if (minY === maxY) { minY -= 5; maxY += 5; }
  }

  const handleIntervalChange = async (opt) => {
    setIntervalValue(opt);
    if (onIntervalChange) {
      onIntervalChange('acceleration', opt);
    }
  };

  // Adaug funcția de export CSV pentru accelerometru și giroscop
  function exportCsv(data, imei) {
    const now = Date.now();
    const fifteenMinutesAgo = now - 15 * 60 * 1000;
    const filtered = data.filter(d =>
      d.imei === imei && new Date(d.created_at).getTime() >= fifteenMinutesAgo
    );
    const header = 'timestamp,imei,acceleration_x,acceleration_y,acceleration_z,gyroscope_x,gyroscope_y,gyroscope_z\n';
    const rows = filtered.map(d => {
      const motion = typeof d.motion_sensors === 'string' ? JSON.parse(d.motion_sensors) : d.motion_sensors;
      const gyroscope = typeof d.gyroscope_sensors === 'string' ? JSON.parse(d.gyroscope_sensors) : d.gyroscope_sensors;
      return [
        d.created_at,
        d.imei,
        (motion?.acceleration_x || 0) * 9.81,
        (motion?.acceleration_y || 0) * 9.81,
        (motion?.acceleration_z || 0) * 9.81,
        gyroscope?.xg ?? '',
        gyroscope?.yg ?? '',
        gyroscope?.zg ?? ''
      ].join(',');
    });
    const csv = header + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `motion_sensors_${imei}_${new Date().toISOString().slice(0,16).replace(/[:T]/g,'-')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ marginBottom: 0, height: 160, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
      <div style={{ display: 'flex', alignItems: 'center', fontWeight: 500, fontSize: 12, marginBottom: 2, color, textTransform: 'uppercase', letterSpacing: 1, gap: 7, flexWrap: 'wrap' }}>
        <span style={{fontSize: 12}}>ACCELEROMETER & GYROSCOPE</span>
        {isLive && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 3, marginLeft: 6 }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#27ae60', animation: 'pulse 1s infinite' }} />
            <span style={{ fontWeight: 600, color: '#27ae60', fontSize: 11 }}>LIVE</span>
          </span>
        )}
        {submenuOptions.map(opt => (
          <button
            key={opt}
            onClick={() => handleIntervalChange(opt)}
            style={{
              padding: '1px 7px',
              borderRadius: 14,
              border: interval === opt ? '2px solid #3498db' : '1px solid #bbb',
              background: interval === opt ? '#eaf6ff' : '#fff',
              color: interval === opt ? '#3498db' : '#222',
              fontWeight: interval === opt ? 'bold' : 'normal',
              fontSize: 10,
              cursor: 'pointer',
              boxShadow: interval === opt ? '0 0 0 2px #3498db55' : '0 1px 2px #eee',
              transition: 'all 0.2s',
              outline: interval === opt ? 'none' : undefined,
              marginLeft: 3
            }}
            tabIndex={-1}
            title="Perioada de colectare"
          >
            {opt}
          </button>
        ))}
        {/* Buton export CSV */}
        <button
          style={{
            marginLeft: 8,
            padding: '2px 8px',
            fontSize: 11,
            borderRadius: 6,
            border: '1px solid #3498db',
            background: '#f5faff',
            color: '#3498db',
            cursor: 'pointer',
            fontWeight: 500
          }}
          title="Exportă CSV toate senzorii"
          onClick={() => exportCsv(data, last?.imei)}
        >
          Export CSV
        </button>
        <span style={{ fontSize: 10, color: '#888', marginLeft: 6, fontWeight: 400 }}>{lastTime}</span>
      </div>
      
      {/* Current values for accelerometer and gyroscope */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 6, 
        marginBottom: 12, 
        padding: '10px 14px', 
        background: 'rgba(255,255,255,0.9)', 
        borderRadius: 10, 
        border: '1px solid #ddd',
        fontSize: 12,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: color, minWidth: 70 }}>Accelerometer:</span>
          <span style={{ color: '#e74c3c', fontWeight: 500 }}>X: {currentValues.accelX.toFixed(3)} m/s²</span>
          <span style={{ color: '#3498db', fontWeight: 500 }}>Y: {currentValues.accelY.toFixed(3)} m/s²</span>
          <span style={{ color: '#27ae60', fontWeight: 500 }}>Z: {currentValues.accelZ.toFixed(3)} m/s²</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: '#e74c3c', minWidth: 70 }}>Gyroscope:</span>
          <span style={{ color: '#e74c3c', fontWeight: 500 }}>X: {currentValues.gyroX.toFixed(3)} °/s</span>
          <span style={{ color: '#3498db', fontWeight: 500 }}>Y: {currentValues.gyroY.toFixed(3)} °/s</span>
          <span style={{ color: '#f39c12', fontWeight: 500 }}>Z: {currentValues.gyroZ.toFixed(3)} °/s</span>
        </div>
      </div>
      <div style={{ height: 280, marginTop: 8 }}>
        <Line ref={chartRef} data={chartData} options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                usePointStyle: true,
                padding: 15,
                font: {
                  size: 13
                },
                boxWidth: 20,
                boxHeight: 8
              }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label + ': ' + context.parsed.y.toFixed(2);
                  if (context.dataset.label.includes('Accel')) {
                    label += ' m/s²';
                  } else if (context.dataset.label.includes('Gyro')) {
                    label += ' °/s';
                  } else if (context.dataset.label.includes('Mag')) {
                    label += ' μT';
                  }
                  return label;
                }
              }
            }
          },
          scales: {
            y: {
              min: minY,
              max: maxY,
              ticks: {
                stepSize: Math.ceil((maxY-minY)/5),
                font: { size: 12 },
                padding: 10
              },
              grid: {
                color: '#eee',
                lineWidth: 1.5,
                drawTicks: true
              }
            },
            x: {
              grid: {
                color: '#f5f5f5',
                lineWidth: 1
              },
              ticks: {
                font: { size: 12 },
                padding: 8,
                maxTicksLimit: 8
              }
            }
          }
        }} />
      </div>
    </div>
  );
}

export default AccelerationChart; 